import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import type { FC } from "react";
import React from "react";
import Path from "src/constants/path";
import { createAppPath } from "src/lib/createAppPath";
import translate from "src/lib/translate";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import {
  ItemType,
  SubscriptionInterval
} from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";
import UserCubit from "src/state/UserCubit/UserCubit";
import UserEmailVerificationCubit from "src/state/UserEmailVerificationCubit/UserEmailVerificationCubit";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import {
  subscriptionState,
  toast,
  tracker,
  useBloc,
  userState
} from "src/state/state";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import Prompt from "src/ui/components/Prompt/Prompt";
import status = SubscriptionDetailsResponse.status;
import createPath from "src/lib/createPath";
import { isHybridApp } from "src/lib/platform";
import { useBrowser } from "src/hybrid/components/Browser";
import { useNavigate } from "react-router-dom";

const promptCanBeDisplayedOnCurrentPage = (paths: Path[] = []) => {
  const defaultPaths = [Path.profile, Path.labResults, Path.myPlan, Path.chat];
  return [...defaultPaths, ...paths].some((path) =>
    location.pathname.endsWith(createAppPath(path, "app"))
  );
};

export const Prompts: FC = () => {
  const [
    ,
    {
      filterAllSubscriptions,
      getSubscriptionPurchaseItems,
      hasActiveSubscription,
      hasFinishedSubscription
    }
  ] = useBloc(SubscriptionCubit);
  const [user] = useBloc(UserCubit);
  const [, { initiateVerification }] = useBloc(UserEmailVerificationCubit, {
    subscribe: false,
    create: () => new UserEmailVerificationCubit()
  });
  const [, { lastSignupFunnel }] = useBloc(UserPreferencesCubit);
  const navigate = useNavigate();
  const [browser] = useBrowser();

  const resendVerificationEmailHandle = async (): Promise<void> => {
    await initiateVerification(user.userData?.email);

    toast.show("verify_email_prompt_notification");
  };

  const userHasEmail = Boolean(userState.state.userData?.email);

  const isSubscriptionPaused = Boolean(
    filterAllSubscriptions({
      status: [status.PAUSED],
      duration: [SubscriptionInterval.monthly, SubscriptionInterval.quarterly]
    }).length
  );

  const subscriptionContainsExpiredItems = filterAllSubscriptions({
    status: [status.ACTIVE, status.IN_REVIEW]
  }).some((subscription) => {
    const items = getSubscriptionPurchaseItems(
      subscription.id,
      ItemType.MEDICATION_SUGGESTION
    );
    return items.some((item) => item.expiredAt);
  });
  const canBeDisplayedOnCurrentPage = promptCanBeDisplayedOnCurrentPage();
  const subscriptionExpiredItemsPromptVisible =
    canBeDisplayedOnCurrentPage && subscriptionContainsExpiredItems;

  const subscriptionPausedPromptVisible =
    canBeDisplayedOnCurrentPage && isSubscriptionPaused;

  //show prompt on specific pages and when email is not verified in user properties
  const emailVerifyPromptVisible =
    canBeDisplayedOnCurrentPage &&
    !subscriptionPausedPromptVisible &&
    !subscriptionExpiredItemsPromptVisible &&
    !user.userData?.emailVerified;

  const subscriptionInactivePromptVisible =
    promptCanBeDisplayedOnCurrentPage([Path.home]) &&
    hasFinishedSubscription === true &&
    hasActiveSubscription === false;

  const handleReEnroll = async () => {
    if (!lastSignupFunnel) {
      return;
    }

    // Ensure subscriptions are loaded before determining incomplete steps
    await Promise.all([
      userState.loadRequiredAppUserData(),
      userState.loadUserAnsweredQuestionnaires(),
      subscriptionState.loadAllSubscriptions()
    ]);

    const signupBloc = new SignupCustomBloc({
      parameters: {
        campaign: lastSignupFunnel
      },
      options: {
        disableInit: true
      }
    });

    const firstIncompleteStep = await signupBloc.findFirstIncompleteStep();
    const params = new URLSearchParams(window.location.search);
    const queryString = params.toString();

    const stepPath = firstIncompleteStep?.path ?? "";
    const url = `/signup/${lastSignupFunnel}${stepPath ? `/${stepPath}` : ""}?${queryString}`;

    tracker.track("Reactivate Subscription clicked", {
      data: {
        last_signup_funnel: lastSignupFunnel,
        url: url
      }
    });

    if (isHybridApp()) {
      void browser.open(createPath(url));
    } else {
      navigate(createPath(url));
    }
  };

  React.useEffect(() => {}, []);
  return (
    <>
      <Prompt
        title={translate("verify_email_prompt_title")}
        message={user.userData?.email ?? ""}
        action={resendVerificationEmailHandle}
        visible={emailVerifyPromptVisible && userHasEmail}
        buttonText={translate("verify_email_prompt_button")}
      />

      <Prompt
        title={translate("subscription_inactive_prompt_title")}
        action={handleReEnroll}
        buttonText={translate("reactivateSubscription")}
        visible={subscriptionInactivePromptVisible}
      />

      <Prompt
        title={translate("subscription_paused_prompt_title")}
        visible={subscriptionPausedPromptVisible}
        textCenter
      />

      <Prompt
        title={translate("subscription_expired_items_prompt_title")}
        visible={subscriptionExpiredItemsPromptVisible}
        textCenter
        dangerBackground
      />
    </>
  );
};
